import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import React from "react";
import HttpService from "../../hooks/Https-services";
import { getCurrentAuth } from "../../hooks/useAxiosPrivate";
import { APIRoutes } from "../../utils/constants";

// The first subscription request on app load can 401 while the access token is
// being refreshed (the interceptor refreshes but does not retry that request), so
// retry a few times before giving up - otherwise the banner never resolves.
const MAX_FETCH_ATTEMPTS = 3;
const FETCH_RETRY_MS = 1000;

export interface SubscriptionStatusValue {
  /** The Stripe subscription object from get-user-subscription-details, or null. */
  subscription: any | null;
  /** HTPTV-695: outstanding open invoice the user can still settle, or null. */
  pendingPayment: any | null;
  loading: boolean;
  refresh: () => void;
}

export const SubscriptionStatusContext =
  createContext<SubscriptionStatusValue>({
    subscription: null,
    pendingPayment: null,
    loading: false,
    refresh: () => {},
  });

export const useSubscriptionStatus = (): SubscriptionStatusValue =>
  useContext(SubscriptionStatusContext);

/**
 * HTPTV-738: one shared, cached fetch of the user's subscription details for the
 * whole authenticated app. The past_due banner (and future consumers) read from
 * here instead of each calling /get-user-subscription-details on their own.
 */
export const SubscriptionStatusProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [subscription, setSubscription] = useState<any | null>(null);
  const [pendingPayment, setPendingPayment] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    const auth = getCurrentAuth();
    if (!auth?.AccessToken) {
      setSubscription(null);
      setPendingPayment(null);
      return;
    }
    let cancelled = false;
    setLoading(true);

    // HTPTV-738: prefer the lightweight /subscription-status endpoint, which
    // returns the live subscription regardless of status (incl. past_due, the
    // users this banner targets). Fall back to the heavier getUserAccountDetails
    // (data.Subscription) if /subscription-status isn't available yet (e.g. before
    // the backend is deployed). Both are read via pickSub so casing doesn't matter.
    // NOT get-user-subscription-details: it gates on PaymentStatus="paid" and
    // returns null for exactly the failing subs we care about.
    const pickSub = (res: any) =>
      res?.data?.data?.subscription ?? res?.data?.data?.Subscription ?? null;
    // HTPTV-695: the outstanding invoice, if any. Both endpoints return it;
    // casing differs (pendingPayment vs PendingPayment).
    const pickPending = (res: any) =>
      res?.data?.data?.pendingPayment ??
      res?.data?.data?.PendingPayment ??
      null;

    const loadSubscription = async () => {
      try {
        const res = await HttpService.get(APIRoutes.SubscriptionStatus);
        return { sub: pickSub(res), pending: pickPending(res) };
      } catch {
        const res = await HttpService.get(APIRoutes.GetUserAccountDetails);
        return { sub: pickSub(res), pending: pickPending(res) };
      }
    };

    const attempt = async (n: number) => {
      try {
        const { sub, pending } = await loadSubscription();
        if (!cancelled) {
          setSubscription(sub);
          setPendingPayment(pending);
          setLoading(false);
        }
      } catch {
        if (!cancelled && n < MAX_FETCH_ATTEMPTS) {
          setTimeout(() => attempt(n + 1), FETCH_RETRY_MS);
        } else if (!cancelled) {
          setSubscription(null);
          setPendingPayment(null);
          setLoading(false);
        }
      }
    };
    attempt(1);

    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  return (
    <SubscriptionStatusContext.Provider
      value={{ subscription, pendingPayment, loading, refresh }}
    >
      {children}
    </SubscriptionStatusContext.Provider>
  );
};
